import { getApiOrigin } from "./apiBase";
import { resetPwaChromeColor } from "./pwaThemeColor";

const API_ORIGIN = getApiOrigin();

export function mediaUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  let path = String(url);
  // legacy: /api/files/uploads/clients/... → /api/files/clients/...
  if (path.startsWith("/api/files/uploads/")) {
    path = path.replace("/api/files/uploads/", "/api/files/");
  }
  if (!path.startsWith("/")) {
    path = `/api/files/${path}`;
  }
  if (API_ORIGIN) return `${API_ORIGIN}${path}`;
  return path;
}

export const TRAINER_COLOR_PRESETS = [
  { id: "purple", label: "Фиолетовый", color: "#6d28d9" },
  { id: "blue", label: "Синий", color: "#2563eb" },
  { id: "teal", label: "Бирюзовый", color: "#0d9488" },
  { id: "green", label: "Зелёный", color: "#059669" },
  { id: "orange", label: "Оранжевый", color: "#ea580c" },
  { id: "rose", label: "Розовый", color: "#e11d48" },
  { id: "slate", label: "Графит", color: "#475569" },
];

export const PORTAL_THEME_MODES = [
  { id: "light", label: "Светлая", hint: "Светлый фон и карточки" },
  { id: "dark", label: "Тёмная", hint: "Тёмный фон вместо белого" },
];

export const PORTAL_FONT_PRESETS = [
  { id: "inter", label: "Inter", family: '"Inter", system-ui, -apple-system, sans-serif' },
  {
    id: "montserrat",
    label: "Montserrat",
    family: '"Montserrat", "Inter", system-ui, sans-serif',
  },
  {
    id: "manrope",
    label: "Manrope",
    family: '"Manrope", "Inter", system-ui, sans-serif',
  },
  { id: "system", label: "Системный", family: 'system-ui, -apple-system, "Segoe UI", sans-serif' },
];

export function portalFontFamily(fontId) {
  const preset = PORTAL_FONT_PRESETS.find((p) => p.id === fontId);
  return preset?.family || PORTAL_FONT_PRESETS[0].family;
}

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b]
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0"))
    .join("")}`;
}

function mix(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const t = amount > 0 ? 255 : 0;
  const p = Math.abs(amount) / 100;
  return rgbToHex(r + (t - r) * p, g + (t - g) * p, b + (t - b) * p);
}

/** Тёмный или светлый текст поверх акцентного цвета (жёлтый → тёмный, фиолетовый → белый). */
export function accentForeground(hex) {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.58 ? "#0f172a" : "#ffffff";
}

const DEFAULT_THEME = {
  accent: "#6d28d9",
  from: "#7c3aed",
  mid: "#6d28d9",
  to: "#4c1d95",
  glow: "rgba(109, 40, 217, 0.28)",
  accentSoft: "rgba(109, 40, 217, 0.12)",
  onAccent: "#ffffff",
  coverUrl: null,
};

export function buildClientHeroTheme(branding) {
  if (!branding) return { ...DEFAULT_THEME, fontFamily: portalFontFamily(null) };

  const accent =
    branding.portal_accent_color && /^#[0-9A-Fa-f]{6}$/.test(branding.portal_accent_color)
      ? branding.portal_accent_color
      : DEFAULT_THEME.accent;

  const coverUrl = branding.portal_cover_url ? mediaUrl(branding.portal_cover_url) : null;

  const { r, g, b } = hexToRgb(accent);
  const glow = `rgba(${r}, ${g}, ${b}, 0.28)`;

  const fontFamily = portalFontFamily(branding.portal_font_family);

  return {
    accent,
    from: mix(accent, 18),
    mid: accent,
    to: mix(accent, -28),
    glow,
    accentSoft: `rgba(${r}, ${g}, ${b}, 0.12)`,
    onAccent: accentForeground(accent),
    coverUrl,
    fontFamily,
    fontId: branding.portal_font_family || "inter",
  };
}

/** Поля персонализации клуба из tenant / API. */
export function clubBrandingSource(tenant) {
  if (!tenant) return null;
  return {
    portal_accent_color: tenant.portal_accent_color,
    portal_cover_url: tenant.portal_cover_url,
    portal_font_family: tenant.portal_font_family,
    portal_theme_mode: resolvePortalThemeMode(tenant),
  };
}

export function resolvePortalThemeMode(source) {
  return source?.portal_theme_mode === "dark" ? "dark" : "light";
}

export function applyPortalThemeMode(mode) {
  const root = document.documentElement;
  if (mode === "dark") {
    root.setAttribute("data-portal-theme", "dark");
  } else {
    root.removeAttribute("data-portal-theme");
  }
}

export function clearPortalThemeMode() {
  document.documentElement.removeAttribute("data-portal-theme");
}

/** Клуб в приоритете над оформлением тренера (как в client dashboard). */
export function mergeClubPortalBranding(tenant, trainerBrand) {
  const club = clubBrandingSource(tenant);
  const hasClub = !!(
    club?.portal_accent_color ||
    club?.portal_cover_url ||
    club?.portal_font_family ||
    club?.portal_theme_mode === "dark"
  );
  if (!trainerBrand && !hasClub) return club;
  return {
    ...trainerBrand,
    portal_accent_color: club?.portal_accent_color || trainerBrand?.portal_accent_color,
    portal_cover_url: club?.portal_cover_url || trainerBrand?.portal_cover_url,
    portal_font_family: club?.portal_font_family || trainerBrand?.portal_font_family,
    portal_theme_mode: club?.portal_theme_mode ?? resolvePortalThemeMode(trainerBrand),
  };
}

export function heroBackgroundStyle(theme) {
  if (!theme) return {};
  if (theme.coverUrl) {
    return {
      backgroundImage: `linear-gradient(to top, rgba(15, 23, 42, 0.82) 0%, rgba(15, 23, 42, 0.35) 55%, rgba(15, 23, 42, 0.15) 100%), url(${theme.coverUrl})`,
      backgroundSize: "cover",
      backgroundPosition: "center center",
    };
  }
  return {
    backgroundColor: theme.accent,
  };
}

/** CSS-переменные для .shell (aa — админ, ca — клиент). */
export function portalShellStyleVars(theme, prefix = "aa") {
  if (!theme) return {};
  if (prefix === "ca") {
    return {
      "--ca-accent": theme.accent,
      "--ca-accent-dark": theme.to,
      "--ca-on-accent": theme.onAccent,
      "--ca-accent-soft": theme.accentSoft,
      "--ca-accent-glow": theme.glow,
      "--ca-hero-from": theme.from,
      "--ca-hero-mid": theme.mid,
      "--ca-hero-to": theme.to,
      fontFamily: theme.fontFamily,
    };
  }
  return {
    "--aa-accent": theme.accent,
    "--aa-on-accent": theme.onAccent,
    "--aa-accent-dark": theme.to,
    "--aa-accent-soft": theme.accentSoft,
    "--aa-accent-glow": theme.glow,
    "--aa-font": theme.fontFamily,
    fontFamily: theme.fontFamily,
  };
}

const BRANDING_CSS_KEYS = [
  "--aa-accent",
  "--aa-accent-dark",
  "--aa-accent-soft",
  "--aa-accent-glow",
  "--aa-font",
  "--ca-accent",
  "--ca-accent-dark",
  "--ca-accent-soft",
  "--ca-accent-glow",
  "--ca-hero-from",
  "--ca-hero-mid",
  "--ca-hero-to",
  "--ds-primary-50",
  "--ds-primary-100",
  "--ds-primary-500",
  "--ds-primary-600",
  "--ds-primary-700",
  "--color-primary",
  "--color-primary-hover",
  "--color-primary-soft",
  "--color-primary-muted",
  "--sidebar-item-active",
  "--font-sans",
];

/** Все переменные бренда: PWA, UI-kit, clinic.module.css. */
export function portalGlobalCssVars(theme) {
  if (!theme) return {};
  const { r, g, b } = hexToRgb(theme.accent);
  const primary50 = mix(theme.accent, 94);
  const primary100 = mix(theme.accent, 88);
  const sidebarActive = `rgba(${r}, ${g}, ${b}, 0.35)`;

  return {
    ...portalShellStyleVars(theme, "aa"),
    ...portalShellStyleVars(theme, "ca"),
    "--ds-primary-50": primary50,
    "--ds-primary-100": primary100,
    "--ds-primary-500": theme.from,
    "--ds-primary-600": theme.accent,
    "--ds-primary-700": theme.to,
    "--color-primary": theme.accent,
    "--color-primary-hover": theme.to,
    "--color-primary-soft": primary50,
    "--color-primary-muted": primary100,
    "--sidebar-item-active": sidebarActive,
    "--font-sans": theme.fontFamily,
    fontFamily: theme.fontFamily,
  };
}

export function applyPortalBrandingToRoot(theme) {
  const root = document.documentElement;
  const vars = portalGlobalCssVars(theme);
  Object.entries(vars).forEach(([key, value]) => {
    if (key === "fontFamily" && value) {
      root.style.fontFamily = value;
      return;
    }
    if (key.startsWith("--") && value != null) {
      root.style.setProperty(key, value);
    }
  });
}

export function clearPortalBrandingFromRoot() {
  const root = document.documentElement;
  BRANDING_CSS_KEYS.forEach((key) => root.style.removeProperty(key));
  root.style.removeProperty("font-family");
  clearPortalThemeMode();
  resetPwaChromeColor();
}
